/**
 * Conversation Service - Messaging functionality for VA profiles
 * 
 * This service handles creating conversations, sending messages, and managing
 * messaging interactions between employers and VAs. It includes comprehensive
 * security checks and      if (error) {
        throw new Error(`Failed to fetch user conversations: $      if (error) {
        throw new Error(`Failed to fetch unread message count: ${error.message}`);
      }or.message}`);
      }a validation.
 */

import supabase from '../utils/supabase.js';

export const conversationService = {
  /**
   * Create or get existing conversation between two users
   * @param {string} participant1Id - First participant (employer)
   * @param {string} participant2Id - Second participant (VA)
   * @param {string|null} jobId - Optional job context
   * @returns {Promise<Object>} Conversation data
   */
  async createOrGetConversation(participant1Id, participant2Id, jobId = null) {
    try {
      if (!participant1Id || !participant2Id) {
        throw new Error('Both participant IDs are required');
      }

      if (participant1Id === participant2Id) {
        throw new Error('Cannot create conversation with yourself');
      }

      // Find existing conversation between these two users (regardless of job_id)
      // Conversations are now unified by participant pair only
      let existingConversation = null;

      // Use two separate queries to avoid the and() inside or() PostgREST syntax issue
      // which causes a 400 Bad Request on some Supabase versions
      const selectFields = 'id, participant_1, participant_2, job_id, last_message_at, created_at';

      const [{ data: conv1, error: err1 }, { data: conv2, error: err2 }] = await Promise.all([
        supabase
          .from('conversations')
          .select(selectFields)
          .eq('participant_1', participant1Id)
          .eq('participant_2', participant2Id)
          .maybeSingle(),
        supabase
          .from('conversations')
          .select(selectFields)
          .eq('participant_1', participant2Id)
          .eq('participant_2', participant1Id)
          .maybeSingle()
      ]);

      const searchError = err1 || err2;
      if (searchError) {
        throw new Error(`Failed to search conversations: ${searchError.message}`);
      }

      existingConversation = conv1 || conv2;

      // If conversation exists, update job_id if provided and not set
      if (existingConversation) {
        if (jobId && !existingConversation.job_id) {
          const { data: updated, error: updateError } = await supabase
            .from('conversations')
            .update({ job_id: jobId })
            .eq('id', existingConversation.id)
            .select()
            .single();

          if (!updateError && updated) {
            return updated;
          }
        }
        return existingConversation;
      }

      // Create new conversation
      // Only one will be created per participant pair due to UNIQUE constraint
      const conversationData = {
        participant_1: participant1Id,
        participant_2: participant2Id,
        last_message_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      };

      // Only add job_id if it's not null/undefined
      if (jobId !== null && jobId !== undefined) {
        conversationData.job_id = jobId;
      }

      const { data: newConversation, error: createError } = await supabase
        .from('conversations')
        .insert(conversationData)
        .select()
        .single();

      if (createError) {
        throw new Error(`Failed to create conversation: ${createError.message}`);
      }

      return newConversation;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Send a message in a conversation
   * @param {string} conversationId - The conversation ID
   * @param {string} senderId - The sender's user ID
   * @param {string} recipientId - The recipient's user ID
   * @param {string} content - Message content
   * @param {string} messageType - Type of message (text, file, etc.)
   * @param {Array} attachments - Optional attachments
   * @returns {Promise<Object>} Sent message data
   */
  async sendMessage(conversationId, senderId, recipientId, content, messageType = 'text', attachments = []) {
    try {
      if (!conversationId || !senderId || !recipientId || !content) {
        throw new Error('Conversation ID, sender ID, recipient ID, and content are required');
      }

      if (senderId === recipientId) {
        throw new Error('Cannot send message to yourself');
      }

      // Verify conversation exists and sender is a participant
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .select('id, participant_1, participant_2, job_id')
        .eq('id', conversationId)
        .single();

      if (conversationError) {
        throw new Error(`Failed to verify conversation: ${conversationError.message}`);
      }

      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // Verify sender is a participant in the conversation
      if (conversation.participant_1 !== senderId && conversation.participant_2 !== senderId) {
        throw new Error('Sender is not a participant in this conversation');
      }

      // Send the message
      const { data: message, error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: senderId,
          recipient_id: recipientId,
          content: content.trim(),
          message_type: messageType,
          attachments: attachments || [],
          is_read: false,
          sent_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (messageError) {
        throw new Error(`Failed to send message: ${messageError.message}`);
      }

      // Update conversation's last_message_at
      const { error: updateError } = await supabase
        .from('conversations')
        .update({
          last_message_at: new Date().toISOString()
        })
        .eq('id', conversationId);

      if (updateError) {
        // Don't throw error here, message was sent successfully
      }

      return message;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get conversation messages with pagination
   * @param {string} conversationId - The conversation ID
   * @param {string} userId - The requesting user's ID (for security)
   * @param {number} limit - Number of messages to fetch
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Array>} Array of messages
   */
  async getConversationMessages(conversationId, userId, limit = 50, offset = 0) {
    try {
      if (!conversationId || !userId) {
        throw new Error('Conversation ID and user ID are required');
      }

      // Verify user is a participant in the conversation
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .select('participant_1, participant_2')
        .eq('id', conversationId)
        .single();

      if (conversationError) {
        throw new Error(`Failed to verify conversation access: ${conversationError.message}`);
      }

      if (!conversation) {
        throw new Error('Conversation not found');
      }

      if (conversation.participant_1 !== userId && conversation.participant_2 !== userId) {
        throw new Error('Access denied: You are not a participant in this conversation');
      }

      // Fetch messages with sender profile info
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select(`
          id,
          conversation_id,
          sender_id,
          recipient_id,
          content,
          message_type,
          attachments,
          is_read,
          sent_at,
          created_at,
          response_time_minutes,
          is_responded,
          responded_at,
          sender:sender_id(full_name, profile_picture)
        `)
        .eq('conversation_id', conversationId)
        .order('sent_at', { ascending: true })
        .range(offset, offset + limit - 1);

      if (messagesError) {
        throw new Error(`Failed to fetch messages: ${messagesError.message}`);
      }

      return messages || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get user's conversations list
   * @param {string} userId - The user's ID
   * @param {number} limit - Number of conversations to fetch
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Array>} Array of conversations with last message info
   */
  async getUserConversations(userId, limit = 20, offset = 0) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { data: conversations, error } = await supabase
        .from('conversations')
        .select(`
          id,
          participant_1,
          participant_2,
          job_id,
          last_message_at,
          created_at,
          participant_1_profile:participant_1(full_name, profile_picture),
          participant_2_profile:participant_2(full_name, profile_picture),
          job:job_id(title)
        `)
        .or(`participant_1.eq.${userId},participant_2.eq.${userId}`)
        .order('last_message_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw new Error(`Failed to fetch conversations: ${error.message}`);
      }

      // Transform data to include other participant info
      const transformedConversations = conversations?.map(conv => {
        const otherParticipant = conv.participant_1 === userId 
          ? conv.participant_2_profile 
          : conv.participant_1_profile;
        
        return {
          id: conv.id,
          otherParticipantId: conv.participant_1 === userId ? conv.participant_2 : conv.participant_1,
          otherParticipant: {
            name: otherParticipant?.full_name || 'Unknown User',
            avatar: otherParticipant?.profile_picture
          },
          jobContext: conv.job ? {
            id: conv.job_id,
            title: conv.job.title
          } : null,
          lastMessageAt: conv.last_message_at,
          createdAt: conv.created_at
        };
      }) || [];

      return transformedConversations;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mark messages as read
   * @param {string} conversationId - The conversation ID
   * @param {string} userId - The user marking messages as read
   * @returns {Promise<number>} Number of messages marked as read
   */
  async markMessagesAsRead(conversationId, userId) {
    try {
      if (!conversationId || !userId) {
        throw new Error('Conversation ID and user ID are required');
      }

      const { data, error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .eq('recipient_id', userId)
        .eq('is_read', false)
        .select('id');

      if (error) {
        throw new Error(`Failed to mark messages as read: ${error.message}`);
      }

      return data?.length || 0;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get unread message count for user
   * @param {string} userId - The user's ID
   * @returns {Promise<number>} Number of unread messages
   */
  async getUnreadMessageCount(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('recipient_id', userId)
        .eq('is_read', false);

      if (error) {
        throw new Error(`Failed to fetch unread count: ${error.message}`);
      }

      return count || 0;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a conversation (soft delete by archiving)
   * @param {string} conversationId - The conversation ID
   * @param {string} userId - The user requesting deletion
   * @returns {Promise<boolean>} Success status
   */
  async deleteConversation(conversationId, userId) {
    try {
      if (!conversationId || !userId) {
        throw new Error('Conversation ID and user ID are required');
      }

      // Verify user is a participant
      const { data: conversation, error: verifyError } = await supabase
        .from('conversations')
        .select('participant_1, participant_2')
        .eq('id', conversationId)
        .single();

      if (verifyError || !conversation) {
        throw new Error('Conversation not found or access denied');
      }

      if (conversation.participant_1 !== userId && conversation.participant_2 !== userId) {
        throw new Error('Access denied: You are not a participant in this conversation');
      }

      // For now, we'll implement a simple approach by updating a deleted_by field
      // In a production system, you might want more sophisticated soft delete logic
      const deletedByField = conversation.participant_1 === userId ? 'deleted_by_participant_1' : 'deleted_by_participant_2';
      
      const { error } = await supabase
        .from('conversations')
        .update({ [deletedByField]: true })
        .eq('id', conversationId);

      if (error) {
        throw new Error(`Failed to delete conversation: ${error.message}`);
      }

      return true;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Search messages within a conversation
   * @param {string} conversationId - The conversation ID
   * @param {string} userId - The user performing the search
   * @param {string} searchTerm - The search term
   * @param {number} limit - Number of results to return
   * @returns {Promise<Array>} Array of matching messages
   */
  async searchConversationMessages(conversationId, userId, searchTerm, limit = 20) {
    try {
      if (!conversationId || !userId || !searchTerm) {
        throw new Error('Conversation ID, user ID, and search term are required');
      }

      // Verify user is a participant
      const { data: conversation, error: verifyError } = await supabase
        .from('conversations')
        .select('participant_1, participant_2')
        .eq('id', conversationId)
        .single();

      if (verifyError || !conversation) {
        throw new Error('Conversation not found or access denied');
      }

      if (conversation.participant_1 !== userId && conversation.participant_2 !== userId) {
        throw new Error('Access denied: You are not a participant in this conversation');
      }

      // Search messages using text search
      const { data: messages, error } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          message_type,
          sent_at,
          sender_id,
          sender:sender_id(full_name, profile_picture)
        `)
        .eq('conversation_id', conversationId)
        .textSearch('content', searchTerm)
        .order('sent_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw new Error(`Failed to search messages: ${error.message}`);
      }

      return messages || [];
    } catch (error) {
      throw error;
    }
  }
};

export default conversationService;
